import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import NuevaTablaLight from '../components/NuevaTabla/Light/NuevaTablaLight';
import NuevaTablaDark from '../components/NuevaTabla/Dark/NuevaTablaDark';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { ThemeContext } from '../context/Theme';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DashboardSidebar from '../components/Dashboard/DashboardSidebar';
import './dashboard.css';

function getRecentMonths(count) {
  const list = [];
  const hoy = new Date();
  let currentYear = hoy.getFullYear();
  let currentMonth = hoy.getMonth() + 1;

  for (let i = 0; i < count; i++) {
    list.push({ year: currentYear, month: currentMonth });
    currentMonth--;
    if (currentMonth === 0) {
      currentMonth = 12;
      currentYear--;
    }
  }
  return list;
}

function getMonthsInRange(start, end) {
  const list = [];
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : startDate;

  let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (current <= last) {
    list.push({ year: current.getFullYear(), month: current.getMonth() + 1 });
    current.setMonth(current.getMonth() + 1);
  }
  return list;
}

function hasFilterValues(info, startDt) {
  if (startDt) return true;
  if (info.poliza || info.nroStro || info.dominio || info.buscarGeneral2 || info.codp) return true;
  if (info.asignado && info.asignado.length > 0) return true;
  if (info.estado && info.estado.length > 0) return true;
  if (info.catStro && info.catStro.length > 0) return true;
  if (info.tipoStro && info.tipoStro.length > 0) return true;
  if (info.sicobe && info.sicobe.length > 0) return true;
  if (info.prioridad && info.prioridad.length > 0) return true;
  if (info.vencimiento && info.vencimiento.length > 0) return true;
  return false;
}

export default function NuevaTabla() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [cookies, setCookie] = useCookies(['user']);
  //const [denunciasRapido, setDenunciasRapido] = useState([])
  const [denuncias, setDenuncias] = useState([]);
  const [codigos, setCodigos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [opacity, setOpacity] = useState(0);
  const [isCalendar, setIsCalendar] = useState(0);
  const [ampSinLeer, setAmpSinLeer] = useState(null);
  // filtros
  const initialInfo = {
    poliza: '',
    nroStro: '',
    dominio: '',
    asignado: [],
    buscarGeneral2: '',
    estado: [],
    catStro: [],
    tipoStro: [],
    sicobe: [],
    prioridad: [],
    vencimiento: [],
    codp: '',
  };
  const [info, setInfo] = useState(initialInfo);
  const [pendingInfo, setPendingInfo] = useState(initialInfo);
  // valor de filtro para calendario
  const [value, onChange] = useState(null);
  // Estados para el rango
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);

  // Optimización de Historial
  const [loadedMonths, setLoadedMonths] = useState([]);
  const [isFetchingExtra, setIsFetchingExtra] = useState(false);
  const loadedMonthsRef = useRef(new Set());
  const [rangoBusqueda, setRangoBusqueda] = useState('2meses');
  const rangoBusquedaRef = useRef('2meses');

  const hoy = new Date();
  const totalMonthsSince2010 = (hoy.getFullYear() - 2010) * 12 + (hoy.getMonth() + 1);

  // Array a buscar en db y uso para filtros
  let updatedList = denuncias;
  //let fastList = denunciasRapido

  const fetchMonths = useCallback(async (monthsList, force = false) => {
    const toFetch = monthsList.filter(m => {
      const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
      return !loadedMonthsRef.current.has(key);
    });

    if (toFetch.length === 0) {
      console.log('🔍 [fetchMonths] -> No hay meses nuevos para buscar. Todo el rango solicitado ya está cargado.');
      return;
    }

    setIsFetchingExtra(true);
    toFetch.forEach(m => {
      const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
      loadedMonthsRef.current.add(key);
    });
    setLoadedMonths(Array.from(loadedMonthsRef.current));

    for (let i = 0; i < toFetch.length; i++) {
      const m = toFetch[i];
      const mesStr = m.month.toString().padStart(2, '0');
      const periodoClave = `${m.year}-${mesStr}`;

      if (!force && rangoBusquedaRef.current === '2meses') {
        for (let j = i; j < toFetch.length; j++) {
          const key = `${toFetch[j].year}-${toFetch[j].month.toString().padStart(2, '0')}`;
          loadedMonthsRef.current.delete(key);
        }
        break;
      }

      try {
        const res = await axios.get(`https://back.agsseguros.online/api/buscar/${m.year}/${mesStr}`);
        //const res = await axios.get(`http://localhost:8080/api/buscar/${m.year}/${mesStr}`);
        setDenuncias(prev => {
          const existingIds = new Set(prev.map(d => d.id));
          const nuevos = res.data.reverse().filter(d => !existingIds.has(d.id));

          return [...prev, ...nuevos];
        });

        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (err) {
        console.error(`❌ [fetchMonths] -> Error crítico al consultar el periodo [${periodoClave}]:`, err);
        const key = `${m.year}-${m.month.toString().padStart(2, '0')}`;
        loadedMonthsRef.current.delete(key);
      }
    }

    setLoadedMonths(Array.from(loadedMonthsRef.current));
    setIsFetchingExtra(false);
  }, []);

  const getMonthsSince2010 = () => {
    const months = [];
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();
    const mesActual = fechaActual.getMonth() + 1;

    for (let año = añoActual; año >= 2010; año--) {
      const mesInicio = año === añoActual ? mesActual : 12;

      for (let mes = mesInicio; mes >= 1; mes--) {
        months.push({
          year: año,
          month: mes,
        });
      }
    }
    return months;
  };

  const handleCambiarRango = useCallback(
    nuevoRango => {
      setRangoBusqueda(nuevoRango);
      rangoBusquedaRef.current = nuevoRango;

      if (nuevoRango === 'todos') {
        setDenuncias([]);
        loadedMonthsRef.current.clear();
        setLoadedMonths([]);

        const historialCompletoMonths = getMonthsSince2010();

        fetchMonths(historialCompletoMonths, true);
      } else {
        setDenuncias([]);
        loadedMonthsRef.current.clear();
        setLoadedMonths([]);

        const recentMonths = getRecentMonths(2);
        fetchMonths(recentMonths, true);
      }
    },
    [fetchMonths]
  );

  useEffect(() => {
    const fetchDataInStages = async () => {
      try {
        const amp = await axios.get('https://back.agsseguros.online/api/ampliaciones/noLeidoAll');
        setAmpSinLeer(amp.data);
        const estadosN = await axios.get('https://back.agsseguros.online/api/estados/estadosAll');
        setEstados(estadosN.data);
        const codigos = await axios.get('https://back.agsseguros.online/api/estados/codigoAll');
        setCodigos(codigos.data);

        // Carga inicial rápida de los últimos 2 meses
        const recentMonths = getRecentMonths(2);
        await fetchMonths(recentMonths, true);

        const asignadoRes = await axios.get(`https://back.agsseguros.online/api/asignadoCarga`, { params: { asignado: cookies.user.usuario } });
        setCookie('asignado', JSON.stringify(asignadoRes.data), { path: '/' });
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };

    fetchDataInStages();
  }, [cookies.user.usuario, setCookie, fetchMonths]);
  useEffect(() => {
    updatedList = denuncias;
  }, [denuncias]);

  let itemsPerPage = 200;
  const [itemOffset, setItemOffset] = useState(0);
  const pageCount = Math.ceil(updatedList.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const handlePageClick = event => {
    const newOffset = (event.selected * itemsPerPage) % updatedList.length;
    setItemOffset(newOffset);
    setCurrentPage(event.selected);
  };

  const handleClick = date => {
    if (!pendingStartDate || (pendingStartDate && pendingEndDate)) {
      setPendingStartDate(date);
      setPendingEndDate(null);
    } else {
      if (date < pendingStartDate) {
        setPendingEndDate(pendingStartDate);
        setPendingStartDate(date);
      } else {
        setPendingEndDate(date);
      }
    }
  };
  const isInRange = date => {
    if (!pendingStartDate) return false;
    if (!pendingEndDate) return date.getTime() === pendingStartDate.getTime();
    return date >= pendingStartDate && date <= pendingEndDate;
  };
  const noFiltrosAplicados = () => {
    return (
      info.poliza === '' &&
      info.codp === '' &&
      info.nroStro === '' &&
      info.dominio === '' &&
      info.asignado.length === 0 &&
      info.estado.length === 0 &&
      info.catStro.length === 0 &&
      info.tipoStro.length === 0 &&
      info.sicobe.length === 0 &&
      info.prioridad.length === 0 &&
      info.vencimiento.length === 0 &&
      info.buscarGeneral2 === '' &&
      value === null
    );
  };
  const sinFiltros = noFiltrosAplicados();
  // Funciones Filtrar y Vaciar
  const handleFiltrar = async () => {
    const formatDateParam = date => {
      if (!date) return null;
      return new Date(date).toISOString().split('T')[0];
    };

    const executeBackendSearch = async (sDate, eDate) => {
      const cleanNro = pendingInfo.nroStro ? pendingInfo.nroStro.replace(/\D/g, '') : '';
      if (cleanNro.length > 0) {
        try {
          setIsFetchingExtra(true);
          const finalNro = cleanNro.padStart(9, '300');
          const resNro = await axios.get(`https://back.agsseguros.online/api/buscarNroInput`, {
            params: { nro: finalNro },
          });
          if (resNro.data && resNro.data.id) {
            const resDetail = await axios.get(`https://back.agsseguros.online/api/verTodoEditar/${resNro.data.id}`);
            if (resDetail.data) {
              const claim = resDetail.data;
              if (!claim.estados) {
                claim.estados = claim.estadoNuevo ? [claim.estadoNuevo] : [];
              }
              if (eDate && claim.fechaDenuncia) {
                const recent = getRecentMonths(12);
                const recentKeys = new Set(recent.map(m => `${m.year}-${m.month.toString().padStart(2, '0')}`));
                const [y, m] = claim.fechaDenuncia.split('-');
                if (recentKeys.has(`${y}-${m.padStart(2, '0')}`)) {
                  setDenuncias([claim]);
                } else {
                  setDenuncias([]);
                }
              } else {
                setDenuncias([claim]);
              }
            } else {
              setDenuncias([]);
            }
          } else {
            setDenuncias([]);
          }
        } catch (err) {
          console.error('Error fetching specific claim by nroStro:', err);
          setDenuncias([]);
        } finally {
          setIsFetchingExtra(false);
        }
      } else {
        try {
          setIsFetchingExtra(true);
          const params = {
            ...pendingInfo,
            prioridad: pendingInfo.prioridad ? pendingInfo.prioridad.map(e => e.value) : [],
            estado: pendingInfo.estado ? pendingInfo.estado.map(e => e.value) : [],
            tipoStro: pendingInfo.tipoStro ? pendingInfo.tipoStro.map(e => e.value) : [],
            asignado: pendingInfo.asignado ? pendingInfo.asignado.map(e => e.value) : [],
            sicobe: pendingInfo.sicobe ? pendingInfo.sicobe.map(e => e.value) : [],
            catStro: pendingInfo.catStro ? pendingInfo.catStro.map(e => e.value) : [],
            vencimiento: pendingInfo.vencimiento ? pendingInfo.vencimiento.map(e => e.value) : [],
            startDate: sDate,
            endDate: eDate,
            isCalendar,
          };
          const res = await axios.get('https://back.agsseguros.online/api/tabla2026/buscarConFiltros', {
            params,
          });
          if (res.data && Array.isArray(res.data)) {
            const mapped = res.data.map(d => {
              if (!d.estados) {
                d.estados = d.estadoNuevo ? [d.estadoNuevo] : [];
              }
              return d;
            });
            setDenuncias(mapped.reverse());
          } else {
            setDenuncias([]);
          }
        } catch (error) {
          console.error('Error fetching filtered history:', error);
          setDenuncias([]);
        } finally {
          setIsFetchingExtra(false);
        }
      }
    };

    if (pendingStartDate) {
      const months = getMonthsInRange(pendingStartDate, pendingEndDate);
      await fetchMonths(months, true);
    } else if (rangoBusqueda === 'todos') {
      if (!hasFilterValues(pendingInfo, null)) {
        setDenuncias([]);
      } else {
        await executeBackendSearch(null, null);
      }
    }
    setInfo({ ...pendingInfo });
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setCurrentPage(0);
    setItemOffset(0);
  };
  const handleVaciar = () => {
    const empty = { ...initialInfo };
    setInfo(empty);
    setPendingInfo(empty);
    onChange(null);
    setStartDate(null);
    setEndDate(null);
    setPendingStartDate(null);
    setPendingEndDate(null);
    setCurrentPage(0);
    setItemOffset(0);
    setRangoBusqueda('2meses');
    rangoBusquedaRef.current = '2meses';

    // Clear and reload recent 2 months
    setDenuncias([]);
    loadedMonthsRef.current.clear();
    setLoadedMonths([]);
    const recentMonths = getRecentMonths(2);
    fetchMonths(recentMonths, true);
  };
  // variables para filtro
  function formatDate(inputDate) {
    const date = new Date(inputDate);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  const getTomorrowDate = () => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return tomorrow.toISOString().split('T')[0];
  };
  const todayDate = getTodayDate();
  const tomorrowDate = getTomorrowDate();
  //
  const parseLocalDate = fechaStr => {
    const [year, month, day] = fechaStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  // FUNCION FILTRO

  const conFiltro = () => {
    if (!startDate && rangoBusqueda === '2meses') {
      const recent = getRecentMonths(2);
      const recentKeys = new Set(recent.map(m => `${m.year}-${m.month.toString().padStart(2, '0')}`));
      updatedList = updatedList.filter(d => {
        if (!d.fechaDenuncia) return false;
        const [y, m] = d.fechaDenuncia.split('-');
        return recentKeys.has(`${y}-${m.padStart(2, '0')}`);
      });
    }
    if (pendingInfo.buscarGeneral2) {
      // Pasamos el término de búsqueda a minúsculas y limpiamos espacios de los extremos
      const searchInput = pendingInfo.buscarGeneral2.toString().toLowerCase().trim();

      updatedList = updatedList.filter(d => {
        // Helper para convertir cualquier campo de la denuncia a un string limpio en minúsculas
        const checkField = field => {
          if (field === null || field === undefined) return '';
          return field.toString().toLowerCase().trim();
        };

        // Campos simples (números o códigos)
        const nroStro = checkField(d.nroStro);
        const entryId = checkField(d.entryId);
        const pza = checkField(d.pza);
        const dom = checkField(d.dom);

        // Campos de texto (Asegurado, Terceros, etc.)
        const nombreAseg = checkField(d.nombreAseg);
        const dominioTercero1 = checkField(d.dominioTercero1);
        const propietarioTercero1 = checkField(d.propietarioTercero1);

        // Evaluamos si el buscador coincide con alguno de los campos
        return (
          nroStro.includes(searchInput) ||
          entryId.includes(searchInput) ||
          pza.includes(searchInput) ||
          dom.includes(searchInput) ||
          nombreAseg.includes(searchInput) ||
          dominioTercero1.includes(searchInput) ||
          propietarioTercero1.includes(searchInput)
        );
      });
    }
    /*
        if (info.buscarGeneral2) {
            fastList = fastList.filter(
                d =>
                    JSON.stringify(d.nroStro).includes(info.buscarGeneral2) ||
                    JSON.stringify(d.entryId).includes(info.buscarGeneral2) ||
                    JSON.stringify(d.pza).includes(info.buscarGeneral2) ||
                    JSON.stringify(d.dom).includes(info.buscarGeneral2) ||
                    (d.nombreAseg &&
                        JSON.stringify(d.nombreAseg)
                            .toLowerCase()
                            .includes(
                                JSON.stringify(info.buscarGeneral2)
                                    .toLowerCase()
                                    .replace(/[&\\#,+()$~%.'":*?<>{}]/g, '')
                            )) ||
                    (d.dominioTercero1 &&
                        JSON.stringify(d.dominioTercero1)
                            .toLowerCase()
                            .includes(
                                JSON.stringify(info.buscarGeneral2)
                                    .toLowerCase()
                                    .replace(/[&\\#,+()$~%.'":*?<>{}]/g, '')
                            )) ||
                    (d.propietarioTercero1 &&
                        JSON.stringify(d.propietarioTercero1)
                            .toLowerCase()
                            .includes(
                                JSON.stringify(info.buscarGeneral2)
                                    .toLowerCase()
                                    .replace(/[&\\#,+()$~%.'":*?<>{}]/g, '')
                            ))
            )
        }
        */
    if (pendingInfo.poliza) {
      updatedList = updatedList.filter(d => JSON.stringify(Number(d.pza)).includes(Number(pendingInfo.poliza)));
    }
    if (pendingInfo.codp) {
      updatedList = updatedList.filter(d => JSON.stringify(Number(d.codp)).includes(Number(pendingInfo.codp)));
    }
    if (pendingInfo.nroStro) {
      updatedList = updatedList.filter(d => JSON.stringify(Number(d.nroStro)).includes(Number(pendingInfo.nroStro)));
    }
    if (pendingInfo.dominio) {
      updatedList = updatedList.filter(d =>
        JSON.stringify(d.dom)
          .replace(/[&\\#,+()$~%.'":*?<>{}]/g, '')
          .toUpperCase()
          .includes(pendingInfo.dominio.toUpperCase())
      );
    }
    if (pendingInfo.asignado.length > 0) {
      updatedList = updatedList.filter(d => {
        return pendingInfo.asignado.some(ele => {
          return ele.value === d.empleadoAsignado;
        });
      });
    }
    if (pendingInfo.tipoStro.length > 0) {
      updatedList = updatedList.filter(d => {
        return pendingInfo.tipoStro.some(ele => {
          return ele.value === d.tipoStro;
        });
      });
    }
    if (pendingInfo.estado.length > 0) {
      updatedList = updatedList.filter(d => {
        return pendingInfo.estado.some(ele => d.estados.includes(ele.value));
      });
    }
    if (pendingInfo.catStro.length > 0) {
      updatedList = updatedList.filter(d => {
        if (!d.catStro) return false;
        const catStroValues = (d.catStro?.toString().match(/.{1,3}/g) || []).map(v => v.toString());
        const infoValues = pendingInfo.catStro.map(ele => ele.value.toString());
        return infoValues.some(value => catStroValues.includes(value));
      });
    }
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = endDate ? new Date(endDate) : start;
      end.setHours(0, 0, 0, 0);
      updatedList = updatedList.filter(d => {
        let fechaStr;
        if (isCalendar == 0) fechaStr = d.fechaDenuncia;
        else if (isCalendar == 1) fechaStr = d.fechaVencimiento;
        else if (isCalendar == 2) fechaStr = d.recepcionRecChoques;
        else if (isCalendar == 3) fechaStr = d.fechaEnviadoCotizar;
        if (!fechaStr) return false;
        const itemDate = parseLocalDate(fechaStr);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= start && itemDate <= end;
      });
    }

    /*
        if (isCalendar == 0) {
            if (formatDate(value) !== '01/01/1970') {
                fastList = fastList.filter(d => formatDate(d.fechaDenuncia) === formatDate(value))
            } else if (formatDate(value) === '01/01/1970') {
            }
        } else if (isCalendar == 1) {
            if (formatDate(value) !== '01/01/1970') {
                fastList = fastList.filter(d => formatDate(d.fechaVencimiento) === formatDate(value))
            } else if (formatDate(value) === '01/01/1970') {
            }
        } else if (isCalendar == 2) {
            if (formatDate(value) !== '01/01/1970') {
                fastList = fastList.filter(d => formatDate(d.recepcionRecChoques) === formatDate(value))
            } else if (formatDate(value) === '01/01/1970') {
            }
        }
            */
    if (pendingInfo.sicobe.length > 0) {
      updatedList = updatedList.filter(d => {
        return pendingInfo.sicobe.some(ele => {
          return ele.value === d.cbtSicobe;
        });
      });
    }
    if (pendingInfo.prioridad.length > 0) {
      updatedList = updatedList.filter(d => {
        return pendingInfo.prioridad.some(ele => {
          return ele.value === d.prioridad;
        });
      });
    }
    if (pendingInfo.vencimiento.length > 0) {
      updatedList = updatedList.filter(d => {
        const fechaVencimiento = new Date(d.fechaVencimiento);
        if (isNaN(fechaVencimiento)) {
          return false;
        }
        const todayMatch = pendingInfo.vencimiento.some(filter => filter.value === 1) && d.fechaVencimiento === todayDate;
        const tomorrowMatch = pendingInfo.vencimiento.some(filter => filter.value === 2) && d.fechaVencimiento === tomorrowDate;
        const expiredMatch = pendingInfo.vencimiento.some(filter => filter.value === 3) && fechaVencimiento.toISOString().split('T')[0] < new Date().toISOString().split('T')[0];
        return todayMatch || tomorrowMatch || expiredMatch;
      });
    }
    if (cookies.user.id === 107) {
      updatedList = updatedList.filter(d => d.estados.some(e => e === 302 || e === 311));
    }
    /*
        if (cookies.user.id === 107) {
            fastList = fastList.filter(d => 302 === d.estadoNuevo || d.estadoNuevo === 311)
        }
        */
  };
  conFiltro();
  const optionsTipoStro = [
    { label: 'ACCIDENTE DE TRANSITO', value: 'ACCIDENTE DE TRANSITO' },
    { label: 'CONTRACTUAL', value: 'CONTRACTUAL' },
    { label: 'ROBO TOTAL', value: 'ROBO TOTAL' },
    { label: 'ROBO PARCIAL', value: 'ROBO PARCIAL' },
    { label: 'ROBO PARCIAL INSP', value: 'ROBO PARCIAL INSP' },
    { label: 'INCENDIO TOTAL', value: 'INCENDIO TOTAL' },
    { label: 'INCENDIO PARCIAL', value: 'INCENDIO PARCIAL' },
    { label: 'DAÑO TOTAL', value: 'DAÑO TOTAL' },
    { label: 'DAÑO PARCIAL', value: 'DAÑO PARCIAL' },
  ];
  const responsabilidadInput = [
    { label: 'Cr', value: 320 },
    { label: 'Cc', value: 320 },
    { label: 'Sr', value: 300 },
    { label: 'Rp', value: 400 },
    { label: 'Rp1 (1 ruedas)', value: 401 },
    { label: 'Rp2 (2 ruedas)', value: 402 },
    { label: 'Rp3 (3 ruedas)', value: 403 },
    { label: 'Rp4 (4 ruedas)', value: 404 },
    { label: 'Rp5 (5 ruedas)', value: 405 },
    { label: 'Rt', value: 500 },
    { label: 'Ra', value: 550 },
    { label: 'Raf', value: 551 },
    { label: 'Ip', value: 600 },
    { label: 'Dp', value: 800 },
    { label: 'Dt', value: 900 },
    { label: 'Aseg les', value: 100 },
    { label: 'Aseg muerto', value: 130 },
    { label: '3ro les', value: 200 },
    { label: '3ro muerto', value: 230 },
    { label: '3ro muerto Rec', value: 233 },
  ];
  const responsabilidadChoques = [
    { label: 'Cr', value: 320 },
    { label: 'Cc', value: 320 },
    { label: 'Sr', value: 300 },
    { label: '3ro les', value: 200 },
    { label: '3ro muerto', value: 230 },
    { label: '3ro muerto Rec', value: 233 },
  ];
  const responsabilidadRoboParcialInsp = [
    { label: 'Rp1 (1 ruedas)', value: 401 },
    { label: 'Rp2 (2 ruedas)', value: 402 },
    { label: 'Rp3 (3 ruedas)', value: 403 },
    { label: 'Rp4 (4 ruedas)', value: 404 },
    { label: 'Rp5 (5 ruedas)', value: 405 },
    { label: 'Ra', value: 550 },
    { label: 'Raf', value: 551 },
  ];
  const responsabilidadRoboTotal = [{ label: 'Rt', value: 500 }];
  const responsabilidadRoboParcial = [{ label: 'Rp', value: 400 }];
  const responsabilidadDanioParcial = [{ label: 'Dp', value: 800 }];
  const responsabilidadIncendioTotal = [];
  const responsabilidadIncendioParcial = [{ label: 'Ip', value: 600 }];
  const responsabilidadDanioTotal = [{ label: 'Dt', value: 900 }];
  const optionsCarga = [
    { value: 'JuanG', label: 'JuanG', key: 1 },
    { value: 'LucaP', label: 'LucaP', key: 2 },
    { value: 'StellaM', label: 'StellaM', key: 3 },
    { value: 'DanielaM', label: 'DanielaM', key: 4 },
    { value: 'rosario', label: 'rosario', key: 5 },
  ];
  const optionsChoque = [
    { value: 'JuanG', label: 'JuanG', key: 1 },
    { value: 'LucaP', label: 'LucaP', key: 2 },
    { value: 'DanielaM', label: 'DanielaM', key: 3 },
    { value: 'DiegoD', label: 'DiegoD', key: 4 },
    { value: 'DiegoS', label: 'DiegoS', key: 5 },
    { value: 'OrelyC', label: 'OrelyC', key: 6 },
    { value: 'StellaM', label: 'StellaM', key: 7 },
    { value: 'LucianaF', label: 'LucianaF', key: 8 },
    { value: 'CandelaS', label: 'CandelaS', key: 9 },
    { value: 'MagaliG', label: 'MagaliG', key: 10 },
    { value: 'PilarS', label: 'PilarS', key: 11 },
    { value: 'Maurom', label: 'Maurom', key: 12 },
    { value: 'ClaudiaF', label: 'ClaudiaF', key: 13 },
    { value: 'Gian', label: 'Gian', key: 14 },
    { value: 'rosario', label: 'rosario', key: 15 },
  ];
  const optionsContractuales = [
    { value: 'JuanG', label: 'JuanG', key: 1 },
    { value: 'LucaP', label: 'LucaP', key: 2 },
    { value: 'DanielaM', label: 'DanielaM', key: 3 },
    { value: 'DiegoD', label: 'DiegoD', key: 4 },
    { value: 'DiegoS', label: 'DiegoS', key: 5 },
    { value: 'OrelyC', label: 'OrelyC', key: 6 },
    { value: 'StellaM', label: 'StellaM', key: 7 },
    { value: 'LucianaF', label: 'LucianaF', key: 8 },
    { value: 'CandelaS', label: 'CandelaS', key: 9 },
    { value: 'MagaliG', label: 'MagaliG', key: 10 },
    { value: 'PilarS', label: 'PilarS', key: 11 },
    { value: 'Augusto', label: 'Augusto', key: 12 },
    { value: 'PaulaC', label: 'PaulaC', key: 13 },
    { value: 'MariaE', label: 'MariaE', key: 14 },
    { value: 'rosario', label: 'rosario', key: 15 },
  ];
  const sicobeOptions = [
    { value: 1, label: 'COBERTURA', key: 1 },
    { value: 2, label: 'EN REVISION', key: 2 },
    { value: 3, label: 'COBERTURA ANALIZADO POR COBRANZAS', key: 3 },
    { value: 4, label: 'SIN COBERTURA ANALIZADO POR COBRANZAS', key: 4 },
    { value: 5, label: 'PARA IMPUTAR', key: 5 },
  ];
  const optionsPrioridad = [
    { label: 'BAJA', value: 1 },
    { label: 'MEDIA', value: 2 },
    { label: 'ALTA', value: 3 },
  ];
  const optionsVencimiento = [
    { label: 'HOY', value: 1 },
    { label: 'MAÑANA', value: 2 },
    { label: 'VENCIDOS', value: 3 },
  ];
  const optionsCalendar = [
    { label: 'Filtro Fecha Recepcion', value: 0 },
    { label: 'Filtro Fecha Vencimiento', value: 1 },
    { label: 'Filtro Fecha Reclamo Recibido', value: 2 },
    { label: 'Fecha Enviado a Cotizar', value: 3 },
  ];
  const handleChange1 = event => {
    setIsCalendar(event.target.value);
  };
  useEffect(() => {}, [theme]);
  const codigosA = cookies?.user?.codigosPermitidos;
  const codigosANumeros = codigosA.split(',').map(c => Number(c.trim()));
  if (cookies.user.id === 100 && sinFiltros) {
    updatedList = updatedList.filter(s => codigosANumeros.includes(s.codp));
  }
  const exportToExcel = () => {
    if (!updatedList || updatedList.length === 0) return;
    conFiltro();
    const dataToExport = updatedList.map(item => ({
      nroStro: item.nroStro?.toString().slice(3) ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stros');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, 'stros.xlsx');
  };
  return (
    <div className={`dashboard-layout ${theme === 0 ? 'dark-mode' : ''}`}>
      <DashboardSidebar user={cookies.user} />
      <main className='dashboard-main' style={{ padding: 0, overflow: 'auto' }}>
        {theme === 1 ? (
          <NuevaTablaLight
            updatedList={updatedList}
            denuncias={denuncias}
            //fastList={fastList}
            isCalendar={isCalendar}
            setIsCalendar={setIsCalendar}
            cookies={cookies}
            ampSinLeer={ampSinLeer}
            estados={estados}
            codigos={codigos}
            setInfo={setInfo}
            sinFiltros={sinFiltros}
            itemOffset={itemOffset}
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            info={info}
            pendingInfo={pendingInfo}
            setPendingInfo={setPendingInfo}
            handleFiltrar={handleFiltrar}
            handleVaciar={handleVaciar}
            onChange={onChange}
            startDate={startDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            endDate={endDate}
            pendingStartDate={pendingStartDate}
            setPendingStartDate={setPendingStartDate}
            pendingEndDate={pendingEndDate}
            setPendingEndDate={setPendingEndDate}
            handleClick={handleClick}
            isInRange={isInRange}
            optionsTipoStro={optionsTipoStro}
            responsabilidadChoques={responsabilidadChoques}
            responsabilidadDanioParcial={responsabilidadDanioParcial}
            responsabilidadDanioTotal={responsabilidadDanioTotal}
            responsabilidadIncendioParcial={responsabilidadIncendioParcial}
            responsabilidadIncendioTotal={responsabilidadIncendioTotal}
            responsabilidadInput={responsabilidadInput}
            responsabilidadRoboParcial={responsabilidadRoboParcial}
            responsabilidadRoboParcialInsp={responsabilidadRoboParcialInsp}
            responsabilidadRoboTotal={responsabilidadRoboTotal}
            optionsCalendar={optionsCalendar}
            optionsCarga={optionsCarga}
            optionsChoque={optionsChoque}
            optionsContractuales={optionsContractuales}
            optionsPrioridad={optionsPrioridad}
            optionsVencimiento={optionsVencimiento}
            sicobeOptions={sicobeOptions}
            handleChange1={handleChange1}
            exportToExcel={exportToExcel}
            loadedMonths={loadedMonths}
            isFetchingExtra={isFetchingExtra}
            totalMonthsSince2010={totalMonthsSince2010}
            rangoBusqueda={rangoBusqueda}
            handleCambiarRango={handleCambiarRango}
          />
        ) : (
          <NuevaTablaDark
            updatedList={updatedList}
            denuncias={denuncias}
            //fastList={fastList}
            isCalendar={isCalendar}
            setIsCalendar={setIsCalendar}
            cookies={cookies}
            ampSinLeer={ampSinLeer}
            estados={estados}
            codigos={codigos}
            setInfo={setInfo}
            sinFiltros={sinFiltros}
            itemOffset={itemOffset}
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            info={info}
            pendingInfo={pendingInfo}
            setPendingInfo={setPendingInfo}
            handleFiltrar={handleFiltrar}
            handleVaciar={handleVaciar}
            onChange={onChange}
            startDate={startDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            endDate={endDate}
            pendingStartDate={pendingStartDate}
            setPendingStartDate={setPendingStartDate}
            pendingEndDate={pendingEndDate}
            setPendingEndDate={setPendingEndDate}
            handleClick={handleClick}
            isInRange={isInRange}
            optionsTipoStro={optionsTipoStro}
            responsabilidadChoques={responsabilidadChoques}
            responsabilidadDanioParcial={responsabilidadDanioParcial}
            responsabilidadDanioTotal={responsabilidadDanioTotal}
            responsabilidadIncendioParcial={responsabilidadIncendioParcial}
            responsabilidadIncendioTotal={responsabilidadIncendioTotal}
            responsabilidadInput={responsabilidadInput}
            responsabilidadRoboParcial={responsabilidadRoboParcial}
            responsabilidadRoboParcialInsp={responsabilidadRoboParcialInsp}
            responsabilidadRoboTotal={responsabilidadRoboTotal}
            optionsCalendar={optionsCalendar}
            optionsCarga={optionsCarga}
            optionsChoque={optionsChoque}
            optionsContractuales={optionsContractuales}
            optionsPrioridad={optionsPrioridad}
            optionsVencimiento={optionsVencimiento}
            sicobeOptions={sicobeOptions}
            handleChange1={handleChange1}
            exportToExcel={exportToExcel}
            loadedMonths={loadedMonths}
            isFetchingExtra={isFetchingExtra}
            totalMonthsSince2010={totalMonthsSince2010}
            rangoBusqueda={rangoBusqueda}
            handleCambiarRango={handleCambiarRango}
          />
        )}
      </main>
    </div>
  );
}
